import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  limit
} from 'firebase/firestore';
import { db } from '../config';
import { LogisticsInvoice, ExpensesInvoice } from '../../types';
import { rebuildExpensesLedger } from './expensesLedgers';

// Expenses Invoices Collection
export const addExpensesInvoice = async (invoice: Omit<LogisticsInvoice, 'id' | 'createdAt'>): Promise<string> => {
  try {
    
    // Check if invoice already exists (exclude drafts - they can be overwritten)
    const existingInvoice = await getExpensesInvoiceByNumber(invoice.invoiceNumber);
    if (existingInvoice && existingInvoice.status !== 'draft') {
      throw new Error(`Expenses invoice with number ${invoice.invoiceNumber} already exists`);
    }
    
    // Use invoice number as the document ID
    const docRef = doc(db, 'expensesInvoices', invoice.invoiceNumber);
    await setDoc(docRef, {
      ...invoice,
      createdAt: serverTimestamp()
    });
    
    // Note: Ledger will be updated when invoice is approved, not when created
    
    return invoice.invoiceNumber;
  } catch (error) {
    throw new Error(`Error adding expenses invoice: ${error}`);
  }
};

export const getExpensesInvoices = async (): Promise<ExpensesInvoice[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'expensesInvoices'));
    
    const result = querySnapshot.docs.map(doc => ({
      id: doc.id, // This will now be the custom key (invoiceNumber-clientName)
      ...doc.data(),
      date: doc.data().date?.toDate() || new Date(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      approvedAt: doc.data().approvedAt?.toDate()
    })) as ExpensesInvoice[];
    
    return result;
  } catch (error) {
    throw new Error(`Error getting expenses invoices: ${error}`);
  }
};

export const getExpensesInvoicesByCompanyId = async (companyId: string): Promise<ExpensesInvoice[]> => {
  try {
    const q = query(
      collection(db, 'expensesInvoices'),
      where('clientId', '==', companyId),
      where('status', '==', 'approved')
    );
    const querySnapshot = await getDocs(q);
    const result = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate() || new Date(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      approvedAt: doc.data().approvedAt?.toDate()
    })) as ExpensesInvoice[];
    
    // Sort by date in descending order after fetching
    result.sort((a, b) => b.date.getTime() - a.date.getTime());
    
    return result;
  } catch (error) {
    throw new Error(`Error getting company expenses invoices: ${error}`);
  }
};

export const getExpensesInvoiceById = async (invoiceId: string): Promise<ExpensesInvoice | null> => {
  try {
    const docRef = doc(db, 'expensesInvoices', invoiceId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id, // This will be the custom key
        ...data,
        date: data.date?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate() || new Date(),
        approvedAt: data.approvedAt?.toDate()
      } as ExpensesInvoice;
    }
    return null;
  } catch (error) {
    throw new Error(`Error getting expenses invoice: ${error}`);
  }
};

// Helper function to get expenses invoice by invoice number only
export const getExpensesInvoiceByNumber = async (invoiceNumber: string): Promise<ExpensesInvoice | null> => {
  try {
    const q = query(
      collection(db, 'expensesInvoices'),
      where('invoiceNumber', '==', invoiceNumber)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate() || new Date(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      approvedAt: doc.data().approvedAt?.toDate()
    } as ExpensesInvoice;
  } catch (error) {
    throw new Error(`Error getting expenses invoice by number: ${error}`);
  }
};

export const getLatestExpensesInvoiceNumber = async (): Promise<string | null> => {
  try {
    const q = query(
      collection(db, 'expensesInvoices'),
      orderBy('invoiceNumber', 'desc'),
      limit(1)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return null;
    }
    const data = snapshot.docs[0].data();
    return typeof data.invoiceNumber === 'string' ? data.invoiceNumber : null;
  } catch (error) {
    console.warn('Error fetching latest expenses invoice number:', error);
    return null;
  }
};

// Helper function to get expenses invoice by invoice number and client name
export const getExpensesInvoiceByNumberAndClient = async (invoiceNumber: string, clientName: string): Promise<ExpensesInvoice | null> => {
  try {
    // Since we now use invoice number as document ID, we can just use getExpensesInvoiceByNumber
    return await getExpensesInvoiceByNumber(invoiceNumber);
  } catch (error) {
    throw new Error(`Error getting expenses invoice by number and client: ${error}`);
  }
};

export const getExpensesInvoicesByUser = async (userId: string): Promise<ExpensesInvoice[]> => {
  try {
    const q = query(
      collection(db, 'expensesInvoices'),
      where('createdBy', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    const result = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate() || new Date(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      approvedAt: doc.data().approvedAt?.toDate()
    })) as ExpensesInvoice[];
    
    // Sort by date in descending order
    result.sort((a, b) => b.date.getTime() - a.date.getTime());
    
    return result;
  } catch (error) {
    throw new Error(`Error getting expenses invoices by user: ${error}`);
  }
};

export const deleteExpensesInvoice = async (invoiceId: string): Promise<void> => {
  try {
    // Fetch invoice to capture clientId before deletion
    const existing = await getExpensesInvoiceById(invoiceId);
    const clientId = existing?.clientId;
    const invoiceRef = doc(db, 'expensesInvoices', invoiceId);
    await deleteDoc(invoiceRef);
    
    // Rebuild the expenses ledger after hard delete
    if (clientId) {
      await rebuildExpensesLedger(clientId);
    }
  } catch (error) {
    throw new Error(`Error deleting expenses invoice: ${error}`);
  }
};

export const updateExpensesInvoice = async (
  invoiceId: string,
  updates: Partial<Omit<ExpensesInvoice, 'id' | 'createdAt' | 'approvedAt'>>
): Promise<void> => {
  try {
    const invoiceRef = doc(db, 'expensesInvoices', invoiceId);
    await updateDoc(invoiceRef, {
      ...updates
    });
    
    // Rebuild the expenses ledger when invoice is updated
    const invoice = await getExpensesInvoiceById(invoiceId);
    if (invoice) {
      await rebuildExpensesLedger(invoice.clientId);
    }
  } catch (error) {
    throw new Error(`Error updating expenses invoice: ${error}`);
  }
};

// Pending Expenses Invoices helpers
export const getPendingExpensesInvoices = async (): Promise<ExpensesInvoice[]> => {
  try {
    const q = query(
      collection(db, 'expensesInvoices'),
      where('status', '==', 'pending')
    );
    const querySnapshot = await getDocs(q);
    const result = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: (doc.data() as any).date?.toDate() || new Date(),
      createdAt: (doc.data() as any).createdAt?.toDate() || new Date(),
      approvedAt: (doc.data() as any).approvedAt?.toDate()
    })) as ExpensesInvoice[];
    // Newest first
    result.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    return result;
  } catch (error) {
    throw new Error(`Error getting pending expenses invoices: ${error}`);
  }
};

export const updateExpensesInvoiceStatus = async (
  invoiceId: string,
  status: LogisticsInvoice['status'],
  approvedBy?: string
): Promise<void> => {
  try {
    const invoiceRef = doc(db, 'expensesInvoices', invoiceId);
    const updateData: any = {
      status,
      approvedAt: serverTimestamp()
    };
    if (approvedBy) {
      updateData.approvedBy = approvedBy;
    }
    await updateDoc(invoiceRef, updateData);
    
    // Rebuild the expenses ledger when invoice status changes
    const invoice = await getExpensesInvoiceById(invoiceId);
    if (invoice) {
      await rebuildExpensesLedger(invoice.clientId);
    }
  } catch (error) {
    throw new Error(`Error updating expenses invoice: ${error}`);
  }
};

// Get latest invoice for a company (expenses)
export const getLatestCompanyInvoice = async (companyId: string): Promise<ExpensesInvoice | null> => {
  try {
    const q = query(
      collection(db, 'expensesInvoices'),
      where('clientId', '==', companyId),
      orderBy('createdAt', 'desc'),
      limit(1)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const doc = querySnapshot.docs[0];
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      date: data.date?.toDate() || new Date(),
      createdAt: data.createdAt?.toDate() || new Date(),
      approvedAt: data.approvedAt?.toDate()
    } as ExpensesInvoice;
  } catch (error) {
    if (error instanceof Error && error.message.includes('index')) {
      // Firebase requires a composite index
    }
    return null;
  }
};
